/**
 * IndexedDB-backed storage for the small amount of local specs editor state that
 * is still worth keeping between refreshes:
 * - uploaded input images
 * - the shared fal.ai API key
 *
 * Product/session state is intentionally not persisted here anymore.
 */

const DB_NAME = "specsEditor";
const STORE_NAME = "session";
const DB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveSession<T>(data: T, key: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(data, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadSession<T>(key: string): Promise<T | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const request = tx.objectStore(STORE_NAME).get(key);
    request.onsuccess = () => resolve((request.result as T) ?? null);
    request.onerror = () => reject(request.error);
  });
}

export async function clearSession(key: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ── Global fal.ai API key (shared across all products) ──────────

const GLOBAL_API_KEY = "__fal_api_key__";
const UPLOAD_STATE_PREFIX = "__upload_state__:";

export async function saveGlobalApiKey(apiKey: string): Promise<void> {
  return saveSession(apiKey, GLOBAL_API_KEY);
}

export async function loadGlobalApiKey(): Promise<string> {
  return (await loadSession<string>(GLOBAL_API_KEY)) ?? "";
}

export async function saveUploadState<T>(key: string, value: T): Promise<void> {
  return saveSession(value, `${UPLOAD_STATE_PREFIX}${key}`);
}

export async function loadUploadState<T>(key: string): Promise<T | null> {
  return loadSession<T>(`${UPLOAD_STATE_PREFIX}${key}`);
}

export async function clearUploadState(key: string): Promise<void> {
  return clearSession(`${UPLOAD_STATE_PREFIX}${key}`);
}
